<template>
    <div>
        <v-navigation-drawer
            v-model="Sidebar_drawer"
            class="leftSidebar"
            :class="{ mini: mini_sidebar }"
            width="300"
            :permanent="!isMobile"
            :scrim="false"
            :rail="mini_sidebar"
            :location="isMobile ? 'bottom' : undefined"
            expand-on-hover
            app
            @mouseenter="hoverExpand"
            @mouseleave="hoverCollapse"
            style="position: fixed; top: 0; left: 0; height: 100vh"
        >
            <div class="pa-5 logo-section">
                <h3 class="logo-title">
                    {{ mini_sidebar ? "M" : $t("logo.title") }}
                </h3>
                <span v-if="!mini_sidebar" class="logo-subtitle">{{
                    $t("logo.subtitle")
                }}</span>
            </div>
            <div class="menu-container">
                <v-list aria-busy="true" aria-label="menu list">
                    <template v-for="(item, i) in sidebarMenu" :key="i">
                        <!-- Sub Headers -->
                        <NavGroup
                            v-if="item.header"
                            :item="item"
                            :mini="mini_sidebar"
                        />

                        <!-- Dividers -->
                        <v-divider v-else-if="item.divider" />

                        <!-- Nested Menus -->
                        <NavCollapse
                            v-else-if="item.children"
                            :item="item"
                            :level="0"
                            :mini="mini_sidebar"
                        />

                        <!-- Single Item -->
                        <NavItem v-else :item="item" :mini="mini_sidebar" />
                    </template>
                </v-list>
            </div>

            <footer v-if="!mini_sidebar" class="sidebar-footer">
                <p>{{ $t("footer.copyright") }}</p>
                <p>{{ $t("footer.poweredBy") }}</p>
            </footer>
        </v-navigation-drawer>
    </div>
</template>

<script>
import { shallowRef } from "vue";
import { mapState, mapMutations } from "vuex";
import sidebarItem from "./sidebarItem";
import NavCollapse from "./navCollapse/NavCollapse.vue";
import NavItem from "./navItem/NavItem.vue";
import NavGroup from "./navGroup/NavGroup.vue";
import LanguageSelector from "@/components/LanguageSelector.vue";

export default {
    components: {
        NavCollapse,
        NavItem,
        NavGroup,
        LanguageSelector,
    },
    data() {
        return {
            /**
             * Reactive reference to the sidebar menu items.
             * It updates dynamically based on the selected language.
             * @type {import('vue').ShallowRef<Array<Object>>}
             */
            sidebarMenu: shallowRef(sidebarItem(this.$t)),
        };
    },
    computed: {
        ...mapState(["Sidebar_drawer", "mini_sidebar", "isMobile"]),
    },
    watch: {
        /**
         * Watches for language changes and reinitializes the sidebar menu.
         * @param {string} newLocale - The new locale value.
         */
        "$i18n.locale"(newLocale) {
            this.sidebarMenu = shallowRef(sidebarItem(this.$t)
            );
        },
    },
    methods: {
        ...mapMutations(["SET_MINI_SIDEBAR", "SET_SIDEBAR_DRAWER"]),

        /**
         * Initializes the sidebar to be expanded by default.
         */
        initializeSidebar() {
            this.SET_MINI_SIDEBAR(false);
        },

        /**
         * Toggles the sidebar state.
         * - On mobile: Opens/closes the sidebar.
         * - On desktop: Switches between expanded and mini mode.
         */
        toggleSidebar() {
            if (this.isMobile) {
                this.SET_SIDEBAR_DRAWER(!this.Sidebar_drawer); // Open/close full sidebar on mobile
                const sidebar = document.querySelector(".leftSidebar");
                if (sidebar) {
                    sidebar.style.display = this.Sidebar_drawer
                        ? "flex"
                        : "none";
                }
            } else {
                this.SET_MINI_SIDEBAR(!this.mini_sidebar); // Toggle mini mode on desktop
            }
        },

        /**
         * Expands the sidebar when hovered over in mini mode.
         */
        hoverExpand() {
            if (this.mini_sidebar) {
                this.SET_MINI_SIDEBAR(false);
            }
        },

        /**
         * Collapses the sidebar when the mouse leaves, only if the sidebar is not open.
         */
        hoverCollapse() {
            if (!this.Sidebar_drawer) {
                this.SET_MINI_SIDEBAR(true);
            }
        },
    },
    created() {
        this.initializeSidebar();
    },
};
</script>

<style scoped>

:deep(.v-list-group__items) {
  --indent-padding: 20px;
}

:deep(.v-list-item-title) {
  text-wrap: auto;
}

:deep(.leftSidebar.mini .v-list) {
    margin-left: 15px !important;
}

/* hide all list headers / subheaders completely */
:deep(.leftSidebar.mini .v-list-subheader) {
    display: none !important;
}

/* center icons, strip padding on every list item */
:deep(.leftSidebar.mini .v-list-item) {
    justify-content: center !important;
    padding-top: 0 !important;
    padding-bottom: 0 !important;
    padding-left: 0 !important;
    padding-right: 0 !important;
}

/* hide every title text */
:deep(.leftSidebar.mini .v-list-item__content),
:deep(.leftSidebar.mini .v-list-item-title) {
    display: none !important;
}

/* collapse group toggles: remove the chevron / arrow */
:deep(.leftSidebar.mini .v-list-group__header .v-list-item__append) {
    display: none !important;
}

/* hide the footer entirely */
:deep(.leftSidebar.mini .sidebar-footer) {
    display: none !important;
}
</style>

